// frontend/src/components/SyncModal.tsx
// Modal de estado de sincronización — Lucide icons, restyle iOS.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { RefreshCw, CheckCircle2, CloudOff, AlertCircle } from 'lucide-react';
import { DUR, EASE_IOS, MODAL_OVERLAY, MODAL_TARJETA, SPRING_POP } from '../lib/motion';

interface SyncModalProps {
  state: 'idle' | 'syncing' | 'success' | 'error';
  error?: string;
  onRetry: () => void;
  onClose: () => void;
}

export function SyncModal({ state, error, onRetry, onClose }: SyncModalProps) {
  // onClose suele ser una función inline: la guardamos en un ref para que
  // los re-renders del padre no reinicien el temporizador de auto-cierre.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (state !== 'success') return;
    const timer = setTimeout(() => onCloseRef.current(), 1500);
    return () => clearTimeout(timer);
  }, [state]);

  return createPortal(
    <AnimatePresence>
    {state !== 'idle' && (
    <m.div
      key="sync-modal"
      {...MODAL_OVERLAY}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,30,64,0.45)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
    >
      <m.div
        {...MODAL_TARJETA}
        layout
        transition={{ layout: { duration: DUR.base, ease: EASE_IOS } }}
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-[24px] shadow-ios-elevated p-7 w-72 text-center overflow-hidden"
      >
        {/* Cada estado entra con un fundido; el ícono de éxito hace "pop" */}
        <m.div
          key={state}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0, transition: { duration: DUR.base, ease: EASE_IOS } }}
          className="flex flex-col items-center gap-4"
        >
        {state === 'syncing' && (
          <>
            <div
              className="w-16 h-16 rounded-[20px] flex items-center justify-center"
              style={{ backgroundColor: '#001e40' }}
            >
              <RefreshCw size={32} strokeWidth={1.8} className="animate-spin-slow" style={{ color: '#fed65b' }} />
            </div>
            <p className="text-ios-headline font-bold text-[#001e40]">Sincronizando...</p>
            <p className="text-ios-subhead text-[#8E8E93]">Actualizando datos con el servidor</p>
          </>
        )}

        {state === 'success' && (
          <>
            <m.div
              initial={{ scale: 0.6 }}
              animate={{ scale: 1, transition: SPRING_POP }}
              className="w-16 h-16 rounded-[20px] flex items-center justify-center"
              style={{ backgroundColor: '#10b981' }}
            >
              <CheckCircle2 size={32} strokeWidth={1.8} style={{ color: 'white' }} />
            </m.div>
            <p className="text-ios-headline font-bold text-[#001e40]">¡Sincronización Exitosa!</p>
          </>
        )}

        {state === 'error' && (() => {
          const isNetworkError =
            !error ||
            error === 'NETWORK_ERROR' ||
            error.toLowerCase().includes('red') ||
            error.toLowerCase().includes('network') ||
            error.toLowerCase().includes('conexion') ||
            error.toLowerCase().includes('conexión');

          const displayMsg = isNetworkError
            ? (error && error !== 'NETWORK_ERROR' ? error : 'Sin conexión al servidor. Los cambios quedarán pendientes hasta el próximo intento.')
            : error;
          return (
            <>
              <div
                className="w-16 h-16 rounded-[20px] flex items-center justify-center"
                style={{ backgroundColor: isNetworkError ? '#ffdad6' : '#fff3cd' }}
              >
                {isNetworkError
                  ? <CloudOff size={32} strokeWidth={1.8} style={{ color: '#ba1a1a' }} />
                  : <AlertCircle size={32} strokeWidth={1.8} style={{ color: '#b45309' }} />
                }
              </div>
              <p className="text-ios-headline font-bold text-[#001e40]">
                {isNetworkError ? 'Sin Conexión' : 'Error al Sincronizar'}
              </p>
              <p className="text-ios-subhead text-[#8E8E93]">{displayMsg}</p>
              <div className="flex gap-2 w-full">
                {isNetworkError && (
                  <button
                    onClick={onRetry}
                    className="flex-1 h-11 rounded-[12px] text-ios-subhead font-semibold text-white tap-active"
                    style={{ backgroundColor: '#001e40' }}
                  >
                    Reintentar
                  </button>
                )}
                <button
                  onClick={onClose}
                  className={`h-11 rounded-[12px] text-ios-subhead font-semibold tap-active ${isNetworkError ? 'flex-1' : 'w-full'}`}
                  style={{ backgroundColor: 'rgba(60,60,67,0.1)', color: '#43474f' }}
                >
                  Cerrar
                </button>
              </div>
            </>
          );
        })()}
        </m.div>
      </m.div>
    </m.div>
    )}
    </AnimatePresence>,
    document.body,
  );
}
